export type ContentHubDisplayFilenameMedia = {
  media_id: string;
  campaign_name?: string | null;
  influencer_handle?: string | null;
  product_type?: string | null;
  contenthub_url?: string | null;
  filename?: string | null;
};

export function normalizeContentHubFilenamePart(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]/g, '');
}

const PRODUCT_TYPE_TOKEN_LABELS: Record<string, string> = {
  INSTAGRAM: 'Instagram',
  TIKTOK: 'TikTok',
  YOUTUBE: 'YouTube',
};

function toPascalToken(token: string): string {
  const normalized = normalizeContentHubFilenamePart(token);
  if (!normalized) return '';
  const lower = normalized.toLowerCase();
  return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
}

export function formatContentHubProductTypeFilenamePart(productType?: string | null): string {
  return (productType ?? '')
    .split('_')
    .map((token) => PRODUCT_TYPE_TOKEN_LABELS[token.toUpperCase()] ?? toPascalToken(token))
    .join('');
}

function getFilenameFromUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed);
    return parsed.pathname.split('/').filter(Boolean).pop() ?? '';
  } catch {
    return trimmed.split('?')[0].split('#')[0].split('/').filter(Boolean).pop() ?? '';
  }
}

function getExtensionFromFilename(filename: string): string {
  const base = filename.trim().split('?')[0].split('#')[0];
  const lastDotIndex = base.lastIndexOf('.');
  if (lastDotIndex <= 0 || lastDotIndex === base.length - 1) return '';
  return base.slice(lastDotIndex).toLowerCase();
}

export function getContentHubFileExtension(
  contenthubUrl?: string | null,
  filename?: string | null,
): string {
  const fromUrl = contenthubUrl ? getExtensionFromFilename(getFilenameFromUrl(contenthubUrl)) : '';
  if (fromUrl) return fromUrl;
  return filename ? getExtensionFromFilename(filename) : '';
}

export function formatContentHubDisplayFilename(media: ContentHubDisplayFilenameMedia): string {
  const mediaIdBase = media.media_id.replace(/-pic$/, '');
  const parts = [
    media.campaign_name ?? '',
    (media.influencer_handle ?? '').replace(/^@/, ''),
    formatContentHubProductTypeFilenamePart(media.product_type),
    mediaIdBase,
  ]
    .map(normalizeContentHubFilenamePart)
    .filter(Boolean);

  const extension = getContentHubFileExtension(media.contenthub_url, media.filename);
  const basename = parts.join(' - ');
  if (basename) return `${basename}${extension}`;

  const fallback = mediaIdBase || media.media_id;
  return fallback ? `${fallback}${extension}` : `download${extension}`;
}
